import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Query client configuration with optimized defaults for the Vera platform
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - data considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache time - data kept in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry configuration
      retry: (failureCount, error: unknown) => {
        // Don't retry on authentication errors (401, 403)
        if ((error as { status?: number })?.status === 401 || (error as { status?: number })?.status === 403) {
          return false;
        }
        // Don't retry on client errors (4xx except 401, 403)
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus only if data is stale
      refetchOnWindowFocus: 'always',
      // Refetch on reconnect
      refetchOnReconnect: 'always',
      // Don't refetch on mount if data exists and is fresh
      refetchOnMount: 'always',
    },
    mutations: {
      // A MUTATION IS NEVER RETRIED (VRG-386).
      //
      // This was `retry: 1`, which was dead configuration until the API client started
      // rejecting on failure: mutations previously RESOLVED on a 4xx/5xx, so react-query never
      // saw a failure and never retried. The moment failures became real, every failed
      // mutation started re-sending, and two things went wrong at once.
      //
      // 1. It HANGS the UI. A mutation whose body is `FormData` (company create, logo upload,
      //    anything multipart) cannot be replayed — the body has already been consumed — so
      //    the retry never completes and the mutation never settles. Observed live: creating a
      //    company with a taken subdomain POSTed once, got a 400, and then sat with a disabled
      //    spinning "Create Company" button forever. No error, no toast, no way out but a
      //    reload — strictly worse than the success toast this ticket set out to fix.
      // 2. It DOUBLE-WRITES. Mutations are non-idempotent by definition. Retrying a POST that
      //    failed *after* the server committed something turns one user action into two.
      //
      // Retrying was never right for this bucket. A failed write is the user's decision to
      // repeat, not ours — they still have the form in front of them, and the button is the
      // retry affordance. (Contrast the queries above, which are idempotent reads and do
      // retry, but only for non-4xx.) If a specific mutation ever genuinely wants a retry, it
      // can set one on its own `useMutation` rather than every write inheriting it.
      retry: 0,
      // Global error handling for mutations
      onError: (error: unknown) => {
        console.error('Mutation error:', error);
        
        // Handle specific error types
        const status = (error as { status?: number })?.status;
        if (status === 401) {
          // Redirect to login or refresh token
          console.warn('Authentication error - user may need to re-login');
        } else if (status === 403) {
          console.warn('Authorization error - user lacks permissions');
        } else if (status && status >= 500) {
          console.error('Server error - please try again later');
        }
      },
      // Global success handling for mutations
      onSuccess: () => {
        // You can add global success handling here if needed
        // For example, invalidating related queries
      },
    },
  },
});

/**
 * Props for QueryProvider
 */
interface QueryProviderProps {
  children: React.ReactNode;
}

/**
 * React Query provider component that wraps the application
 * Provides query client context and devtools for development
 */
export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

/**
 * Hook to access the query client instance
 * Useful for manual cache management and imperative operations
 */
export const useQueryClient = () => {
  return queryClient;
};

/**
 * Utility function to invalidate all queries
 * Useful for global state refresh scenarios
 */
export const invalidateAllQueries = async () => {
  await queryClient.invalidateQueries();
};

/**
 * Utility function to clear all cached data
 * Useful for logout scenarios
 */
export const clearQueryCache = () => {
  queryClient.clear();
};

/**
 * Utility function to set default options at runtime
 * Can be used to update configuration based on user preferences
 */
export const updateQueryDefaults = (options: Parameters<typeof queryClient.setDefaultOptions>[0]) => {
  queryClient.setDefaultOptions(options);
};

export default QueryProvider;
